import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Sparkles, Megaphone } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface ClimbsListFabProps {
  activeTab: 'boulders' | 'routes';
  isStaffMember: boolean;
  onAddClimb: () => void;
  onScan: () => void;
  /** When provided (staff editor/admin), adds a stacked "Add announcement" action. */
  onAddAnnouncement?: () => void;
}

interface FabAction {
  label: string;
  icon: typeof Plus;
  onClick: () => void;
  variant: 'primary' | 'secondary';
  dataTour?: string;
}

const variantClasses: Record<FabAction['variant'], string> = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
};

export function ClimbsListFab({
  activeTab,
  isStaffMember,
  onAddClimb,
  onScan,
  onAddAnnouncement,
}: ClimbsListFabProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const actions: FabAction[] = [
    {
      label: activeTab === 'boulders' ? t('climbCreate.boulder') : t('climbCreate.route'),
      icon: Plus,
      onClick: onAddClimb,
      variant: 'primary',
      dataTour: 'gym-add-climb-button',
    },
    ...(isStaffMember
      ? [
          {
            label: t('climbScan.uploadButton'),
            icon: Sparkles,
            onClick: onScan,
            variant: 'secondary' as const,
          },
        ]
      : []),
    ...(isStaffMember && onAddAnnouncement
      ? [
          {
            label: t('announcement.add'),
            icon: Megaphone,
            onClick: onAddAnnouncement,
            variant: 'secondary' as const,
          },
        ]
      : []),
  ];

  const runAction = (action: FabAction) => {
    setOpen(false);
    action.onClick();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label={t('climbCreate.title')}
          className={cn(
            'fixed z-50 rounded-full h-14 w-14 p-0 shadow-lg',
            'right-4 md:right-6',
            'bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-6',
            'data-[state=open]:rotate-45 transition-transform duration-200',
          )}
        >
          <Plus className="h-6 w-6" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={12}
        className="w-auto p-0 bg-transparent border-none shadow-none"
      >
        <div className="flex flex-col items-end gap-3">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                type="button"
                data-tour={action.dataTour}
                onClick={() => runAction(action)}
                className="flex items-center gap-2 group"
              >
                <span className="text-sm font-medium bg-background/95 text-foreground px-3 py-1.5 rounded-md shadow border">
                  {action.label}
                </span>
                <span
                  className={cn(
                    'inline-flex h-12 w-12 items-center justify-center rounded-full shadow-md transition-transform group-hover:scale-105',
                    variantClasses[action.variant],
                  )}
                >
                  <Icon className="h-5 w-5" />
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
